import React, { useEffect, useState, useCallback } from "react";
import api, { API, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Badge, EmptyState } from "@/components/Common";
import { fmtDate } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Upload, Search, Loader2, Eye, History, UploadCloud, Trash2, FileText } from "lucide-react";
import { toast } from "sonner";

const CATEGORIE = ["contratto", "identita", "cv", "attestato", "fattura", "registro", "decreto", "rendiconto", "modulo", "altro"];

export async function previewDocument(docId, version) {
  const token = localStorage.getItem("indar_token");
  const url = `${API}/documenti/${docId}/download${version ? `?v=${version}` : ""}`;
  const resp = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
  if (!resp.ok) { toast.error("Errore apertura documento"); return; }
  const blob = await resp.blob();
  const blobUrl = URL.createObjectURL(blob);
  window.open(blobUrl, "_blank");
  setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
}

export default function Documenti() {
  const { user } = useAuth();
  const [docs, setDocs] = useState(null);
  const [q, setQ] = useState("");
  const [corsi, setCorsi] = useState([]);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ titolo: "", categoria: "modulo", corso_id: "" });
  const [file, setFile] = useState(null);
  const [versionsDoc, setVersionsDoc] = useState(null);
  const [verFile, setVerFile] = useState(null);

  const load = useCallback(async () => {
    const { data } = await api.get("/documenti", { params: q ? { q } : {} });
    setDocs(data);
  }, [q]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/corsi").then((r) => setCorsi(r.data)); }, []);

  const upload = async () => {
    if (!file) { toast.error("Seleziona un file"); return; }
    if (!form.titolo) { toast.error("Inserisci un titolo"); return; }
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("titolo", form.titolo);
      fd.append("categoria", form.categoria);
      if (form.corso_id) fd.append("corso_id", form.corso_id);
      await api.post("/documenti/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Documento caricato");
      setOpen(false); setFile(null); setForm({ titolo: "", categoria: "modulo", corso_id: "" });
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const uploadVersion = async () => {
    if (!verFile) { toast.error("Seleziona un file"); return; }
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("file", verFile);
      await api.post(`/documenti/${versionsDoc.id}/version`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Nuova versione caricata");
      setVersionsDoc(null); setVerFile(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const remove = async (d) => {
    if (!window.confirm(`Eliminare "${d.titolo}"?`)) return;
    await api.delete(`/documenti/${d.id}`);
    toast.success("Documento eliminato");
    load();
  };

  const docActions = (d) => (
    <div className="flex shrink-0 items-center gap-1">
      {d.storage_path && (
        <button onClick={() => previewDocument(d.id)} className="rounded p-2.5 sm:p-1.5 text-slate-500 hover:bg-slate-100 hover:text-slate-900" title="Anteprima" data-testid="preview-doc">
          <Eye className="h-4 w-4" />
        </button>
      )}
      {user?.can_write && d.storage_path && (
        <button onClick={() => setVersionsDoc(d)} className="rounded p-2.5 sm:p-1.5 text-slate-500 hover:bg-slate-100 hover:text-slate-900" title="Versioni" data-testid="versions-doc">
          <History className="h-4 w-4" />
        </button>
      )}
      {user?.can_write && (
        <button onClick={() => remove(d)} className="rounded p-2.5 sm:p-1.5 text-slate-500 hover:bg-rose-50 hover:text-rose-600" title="Elimina" data-testid="delete-doc">
          <Trash2 className="h-4 w-4" />
        </button>
      )}
    </div>
  );

  return (
    <div data-testid="documenti-page">
      <PageHeader title="Archivio Documentale" subtitle="Upload file, versioni e anteprima — ricercabile per soggetto e corso">
        {user?.can_write && (
          <Button onClick={() => setOpen(true)} data-testid="upload-doc-btn" className="bg-[#E11D48] hover:bg-[#BE123C]">
            <Upload className="mr-1.5 h-4 w-4" /> Carica Documento
          </Button>
        )}
      </PageHeader>

      <div className="mb-4 relative max-w-sm">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca documenti..." className="pl-9" data-testid="documenti-search" />
      </div>

      <div className="rounded-xl border bg-white overflow-hidden">
        {docs === null ? (
          <div className="flex justify-center py-20 text-slate-400"><Loader2 className="h-6 w-6 animate-spin" /></div>
        ) : docs.length === 0 ? (
          <EmptyState title="Nessun documento" hint="Carica il primo file con il pulsante in alto." />
        ) : (
          <>
          <div className="divide-y sm:hidden" data-testid="documenti-cards">
            {docs.map((d) => (
              <div key={d.id} className="p-4" data-testid="documento-card">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-medium text-slate-900 break-words">{d.titolo}</div>
                    {d.original_filename && <div className="mt-0.5 flex items-center gap-1 text-xs text-slate-500"><FileText className="h-3.5 w-3.5 shrink-0" /><span className="truncate">{d.original_filename}</span></div>}
                  </div>
                  {docActions(d)}
                </div>
                <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-500">
                  <Badge label={d.categoria} cls="bg-slate-100 text-slate-600 border-slate-200" />
                  <span className="font-mono">v{d.versione || 1}</span>
                  <span>{fmtDate(d.data_documento)}</span>
                </div>
              </div>
            ))}
          </div>
          <div className="hidden overflow-x-auto sm:block">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50 hover:bg-slate-50">
                  <TableHead className="text-xs uppercase text-slate-500">Titolo</TableHead>
                  <TableHead className="text-xs uppercase text-slate-500">Categoria</TableHead>
                  <TableHead className="text-xs uppercase text-slate-500">File</TableHead>
                  <TableHead className="text-xs uppercase text-slate-500">Versione</TableHead>
                  <TableHead className="text-xs uppercase text-slate-500">Data</TableHead>
                  <TableHead className="w-32" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {docs.map((d) => (
                  <TableRow key={d.id} data-testid="documento-row">
                    <TableCell className="font-medium text-slate-900">{d.titolo}</TableCell>
                    <TableCell><Badge label={d.categoria} cls="bg-slate-100 text-slate-600 border-slate-200" /></TableCell>
                    <TableCell className="text-sm text-slate-500">
                      {d.original_filename ? (
                        <span className="flex items-center gap-1.5"><FileText className="h-4 w-4" /> {d.original_filename}</span>
                      ) : <span className="text-slate-300">—</span>}
                    </TableCell>
                    <TableCell className="font-mono text-xs">v{d.versione || 1}</TableCell>
                    <TableCell className="text-sm">{fmtDate(d.data_documento)}</TableCell>
                    <TableCell>{docActions(d)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          </>
        )}
      </div>

      {/* Upload dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Carica Documento</DialogTitle>
            <DialogDescription>Seleziona un file da archiviare (max 20MB).</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-xs font-semibold uppercase text-slate-500">Titolo</Label>
              <Input value={form.titolo} onChange={(e) => setForm({ ...form, titolo: e.target.value })} className="mt-1" data-testid="doc-titolo" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs font-semibold uppercase text-slate-500">Categoria</Label>
                <Select value={form.categoria} onValueChange={(v) => setForm({ ...form, categoria: v })}>
                  <SelectTrigger className="mt-1" data-testid="doc-categoria"><SelectValue /></SelectTrigger>
                  <SelectContent>{CATEGORIE.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs font-semibold uppercase text-slate-500">Corso (opz.)</Label>
                <Select value={form.corso_id} onValueChange={(v) => setForm({ ...form, corso_id: v })}>
                  <SelectTrigger className="mt-1" data-testid="doc-corso"><SelectValue placeholder="Nessuno" /></SelectTrigger>
                  <SelectContent>{corsi.map((c) => <SelectItem key={c.id} value={c.id}>{c.codice}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <label className="flex cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed border-slate-200 py-8 hover:border-[#FDA4AF] hover:bg-rose-50/30" data-testid="doc-dropzone">
              <UploadCloud className="h-8 w-8 text-slate-400" />
              <span className="mt-2 text-sm font-medium text-slate-700">{file ? file.name : "Clicca per selezionare un file"}</span>
              <span className="mt-0.5 text-xs text-slate-400">PDF, Word, Excel, immagini — max 20MB</span>
              <input type="file" className="hidden" onChange={(e) => setFile(e.target.files[0])} data-testid="doc-file-input" />
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
            <Button onClick={upload} disabled={saving} className="bg-[#E11D48] hover:bg-[#BE123C]" data-testid="doc-upload-save">
              {saving && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />} Carica
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Versions dialog */}
      <Dialog open={!!versionsDoc} onOpenChange={(o) => !o && setVersionsDoc(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Versioni — {versionsDoc?.titolo}</DialogTitle>
            <DialogDescription>Storico versioni e caricamento di una nuova revisione.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2 max-h-72 overflow-y-auto">
            {(versionsDoc?.versions || []).slice().reverse().map((v) => (
              <div key={v.versione} className="flex items-center justify-between rounded-lg border px-3 py-2">
                <div>
                  <div className="text-sm font-medium">v{v.versione} · {v.original_filename}</div>
                  <div className="text-xs text-slate-400">{fmtDate(v.uploaded_at)} · {v.uploaded_by}</div>
                </div>
                <button onClick={() => previewDocument(versionsDoc.id, v.versione)} className="rounded p-2.5 sm:p-1.5 text-slate-500 hover:bg-slate-100" data-testid="preview-version">
                  <Eye className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
          <div className="rounded-lg bg-slate-50 p-3">
            <Label className="text-xs font-semibold uppercase text-slate-500">Carica nuova versione</Label>
            <input type="file" className="mt-2 block w-full text-sm" onChange={(e) => setVerFile(e.target.files[0])} data-testid="version-file-input" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setVersionsDoc(null)}>Chiudi</Button>
            <Button onClick={uploadVersion} disabled={saving} className="bg-[#E11D48] hover:bg-[#BE123C]" data-testid="version-upload-save">
              {saving && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />} Carica Versione
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
